import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import { BugReport, CheckCircle, Error, Warning } from '@mui/icons-material';

/**
 * Componente para diagnóstico de problemas no salvamento de clientes
 */
const DiagnosticoCliente = ({ open, onClose, formData, clientes, colaboradores }) => {
  const [diagnostico, setDiagnostico] = useState(null);

  const executarDiagnostico = () => {
    const problemas = [];
    const avisos = [];
    const sucessos = [];

    // 1. Verificação de campos obrigatórios
    const camposObrigatorios = {
      nome_completo: formData.nome_completo,
      cpf_cnpj: formData.cpf_cnpj,
      email: formData.email,
      celular: formData.celular
    };

    Object.entries(camposObrigatorios).forEach(([campo, valor]) => {
      if (!valor || valor.trim() === '') {
        problemas.push(`Campo obrigatório "${campo}" está vazio`);
      } else {
        sucessos.push(`Campo obrigatório "${campo}" preenchido`);
      }
    });

    // 2. Validação de CPF/CNPJ
    if (formData.cpf_cnpj) {
      const cpfCnpjLimpo = formData.cpf_cnpj.replace(/\D/g, "");
      if (formData.tipo_pessoa === "Física") {
        if (cpfCnpjLimpo.length !== 11) {
          problemas.push("CPF deve ter 11 dígitos");
        } else {
          sucessos.push("CPF com formato correto");
        }
      } else {
        if (cpfCnpjLimpo.length !== 14) {
          problemas.push("CNPJ deve ter 14 dígitos");
        } else {
          sucessos.push("CNPJ com formato correto");
        }
      }
    }

    // 3. Validação de email
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        problemas.push("Email com formato inválido");
      } else {
        sucessos.push("Email com formato válido");
      }
    }

    // 4. Validação de telefone
    if (formData.celular) {
      const telefoneLimpo = formData.celular.replace(/\D/g, "");
      if (telefoneLimpo.length < 10) {
        problemas.push("Telefone deve ter pelo menos 10 dígitos");
      } else {
        sucessos.push("Telefone com formato válido");
      }
    }

    // 5. Verificação de relacionamentos
    if (formData.is_comissionado) {
      if (!formData.comissionado_colaborador_id && !formData.comissionado_cliente_id) {
        problemas.push("Cliente marcado como comissionado mas não há relacionamento definido");
      } else {
        if (formData.comissionado_colaborador_id) {
          const colaboradorExiste = colaboradores.find(c => c.id === formData.comissionado_colaborador_id);
          if (!colaboradorExiste) {
            problemas.push(`Colaborador comissionado (ID: ${formData.comissionado_colaborador_id}) não encontrado`);
          } else {
            sucessos.push(`Colaborador comissionado encontrado: ${colaboradorExiste.nome_completo}`);
          }
        }
        if (formData.comissionado_cliente_id) {
          const clienteExiste = clientes.find(c => c.id === formData.comissionado_cliente_id);
          if (!clienteExiste) {
            problemas.push(`Cliente comissionado (ID: ${formData.comissionado_cliente_id}) não encontrado`);
          } else {
            sucessos.push(`Cliente comissionado encontrado: ${clienteExiste.nome_completo}`);
          }
        }
      }
    }

    // 6. Verificação de dados monetários
    const camposMonetarios = ['salario', 'limite_credito', 'renda_mensal', 'patrimonio'];
    camposMonetarios.forEach(field => {
      if (formData[field] && formData[field] !== '0,00' && formData[field] !== '0.00') {
        const valorNumerico = parseFloat(formData[field].replace(/\D/g, "")) / 100;
        if (isNaN(valorNumerico) || valorNumerico < 0) {
          problemas.push(`Campo monetário "${field}" com valor inválido`);
        } else {
          sucessos.push(`Campo monetário "${field}" com valor válido: R$ ${valorNumerico.toFixed(2)}`);
        }
      }
    });

    // 7. Verificação de data
    if (formData.data_nascimento) {
      const [dia, mes, ano] = formData.data_nascimento.split('/');
      if (dia && mes && ano) {
        const data = new Date(ano, mes - 1, dia);
        if (isNaN(data.getTime())) {
          problemas.push("Data de nascimento inválida");
        } else {
          const hoje = new Date();
          const idade = hoje.getFullYear() - data.getFullYear();
          if (idade < 0 || idade > 150) {
            avisos.push("Data de nascimento pode estar incorreta (idade fora do intervalo esperado)");
          } else {
            sucessos.push(`Data de nascimento válida (idade: ${idade} anos)`);
          }
        }
      } else {
        problemas.push("Data de nascimento com formato inválido");
      }
    }

    // 8. Verificação de CEP
    if (formData.cep) {
      const cepLimpo = formData.cep.replace(/\D/g, "");
      if (cepLimpo.length !== 8) {
        avisos.push("CEP deve ter 8 dígitos");
      } else {
        sucessos.push("CEP com formato correto");
      }
    }

    // 9. Verificação de endereço
    if (formData.logradouro && !formData.numero) {
      avisos.push("Endereço preenchido mas número não informado");
    }

    // 10. Verificação de dados bancários
    if (formData.banco || formData.agencia || formData.conta) {
      if (!formData.banco || !formData.agencia || !formData.conta) {
        avisos.push("Dados bancários incompletos");
      } else {
        sucessos.push("Dados bancários completos");
      }
    }

    // 11. Verificação de consentimento LGPD
    if (formData.consentimento_lgpd === false || formData.consentimento_lgpd === 0) {
      problemas.push("Consentimento LGPD não concedido");
    } else if (formData.consentimento_lgpd === true || formData.consentimento_lgpd === 1) {
      sucessos.push("Consentimento LGPD concedido");
    }

    setDiagnostico({
      problemas,
      avisos,
      sucessos,
      totalProblemas: problemas.length,
      totalAvisos: avisos.length,
      totalSucessos: sucessos.length
    });
  };

  const limparDiagnostico = () => {
    setDiagnostico(null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BugReport color="primary" />
          Diagnóstico de Cliente
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!diagnostico ? (
          <Box>
            <Typography variant="body1" gutterBottom>
              Este diagnóstico irá verificar:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Campos obrigatórios" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Validação de CPF/CNPJ" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Formato de email e telefone" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Relacionamentos de comissionamento" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Dados monetários" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Data de nascimento" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Endereço e CEP" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Dados bancários" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Consentimento LGPD" />
              </ListItem>
            </List>
            
            <Button 
              variant="contained" 
              onClick={executarDiagnostico}
              fullWidth
              sx={{ mt: 2 }}
            >
              Executar Diagnóstico
            </Button>
          </Box>
        ) : (
          <Box>
            {/* Resumo */}
            <Box display="flex" gap={1} mb={2}>
              <Chip 
                icon={<Error />} 
                label={`${diagnostico.totalProblemas} Problemas`} 
                color="error" 
                variant={diagnostico.totalProblemas > 0 ? "filled" : "outlined"}
              />
              <Chip 
                icon={<Warning />} 
                label={`${diagnostico.totalAvisos} Avisos`} 
                color="warning" 
                variant={diagnostico.totalAvisos > 0 ? "filled" : "outlined"}
              />
              <Chip 
                icon={<CheckCircle />} 
                label={`${diagnostico.totalSucessos} OK`} 
                color="success" 
                variant="outlined"
              />
            </Box>

            {/* Problemas */}
            {diagnostico.problemas.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Problemas Encontrados ({diagnostico.problemas.length}):
                </Typography>
                <List dense>
                  {diagnostico.problemas.map((problema, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText primary={`• ${problema}`} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {/* Avisos */}
            {diagnostico.avisos.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Avisos ({diagnostico.avisos.length}):
                </Typography>
                <List dense>
                  {diagnostico.avisos.map((aviso, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText primary={`• ${aviso}`} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {/* Sucessos */}
            {diagnostico.sucessos.length > 0 && (
              <Alert severity="success">
                <Typography variant="subtitle2" gutterBottom>
                  Verificações OK ({diagnostico.sucessos.length}):
                </Typography>
                <List dense>
                  {diagnostico.sucessos.map((sucesso, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText primary={`• ${sucesso}`} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Recomendações */}
            {diagnostico.totalProblemas > 0 && (
              <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom>
                  Recomendações:
                </Typography>
                <Typography variant="body2">
                  • Corrija todos os problemas antes de tentar salvar novamente
                </Typography>
                <Typography variant="body2">
                  • Verifique se todos os campos obrigatórios estão preenchidos
                </Typography>
                <Typography variant="body2">
                  • Confirme se os dados estão no formato correto
                </Typography>
              </Alert>
            )}

            {diagnostico.totalProblemas === 0 && (
              <Alert severity="success">
                <Typography variant="subtitle2">
                  ✅ Dados prontos para salvamento!
                </Typography>
                <Typography variant="body2">
                  Todos os campos obrigatórios estão preenchidos e os dados estão no formato correto.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {diagnostico && (
          <Button onClick={limparDiagnostico}>
            Novo Diagnóstico
          </Button>
        )}
        <Button onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiagnosticoCliente; 